import { SettingsPermissions } from "@redotech/account-common/permissions";
import { csatSettingsToSendSurveyConfig } from "@redotech/effect-api-shared/default-rules/support-settings-mapping";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  getCurrentTeam,
  updateCurrentTeam,
} from "@redotech/redo-merchant-app-common/client/team";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { useBatchUpdateDefaultRuleConfigs } from "@redotech/redo-merchant-app-common/setting/use-batch-update-default-rule-configs";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { normalizeCsatSampleRate } from "@redotech/redo-support-common/csat-sampling";
import { csatSurveyEmail } from "@redotech/redo-support-common/csat-survey";
import { toast } from "@redotech/redo-web/alert";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { PageForm } from "@redotech/redo-web/page-form";
import { SettingSectionWrapper } from "@redotech/redo-web/setting-wrapper/setting-section-wrapper";
import { memo, useContext, useMemo, useState } from "react";
import { EmailPreviewContent } from "../flow-editor/advanced-flow-editor/steps/common/email-preview-content";
import {
  csatForm,
  DEFAULT_SURVEY_EMAIL_BODY_HTML,
  toCsatSurveyMethods,
  toCsatSurveySendTime,
  type CsatValue,
} from "./csat-form";
import { CsatSettingsContext } from "./csat-settings-context";
import { findUnsendableTemplateIds } from "./csat-unsendable-templates";
import * as customerSatisfactionCss from "./customer-satisfaction.module.css";
import { useCsatSurveyMessageEditor } from "./hooks/use-csat-survey-message-editor";
import {
  MISSING_SURVEY_LINK_MESSAGE,
  useCsatSurveyTemplates,
} from "./hooks/use-csat-survey-templates";

/**
 * One form and one save for the whole CSAT settings object, which every CSAT
 * page mounts inside: the save writes `settings.support.csat` wholesale.
 */
export const CsatSettingsShell = memo(function CsatSettingsShell({
  children,
}: {
  children: React.ReactNode;
}) {
  const team = useContext(TeamContext);

  /* Derived during render, not mirrored into state through an effect: the form
     value is a pure function of the team settings. */
  const value: CsatValue | undefined = useMemo(
    () =>
      team
        ? {
            csatEnabled: team.settings.support?.csat?.enabled || false,
            surveySendTime: toCsatSurveySendTime(
              team.settings.support?.csat?.surveySendTime,
            ),
            surveyMethods: toCsatSurveyMethods(
              team.settings.support?.csat?.surveyMethods,
            ),
            allowRemarks: team.settings.support?.csat?.allowRemarks ?? true,
            headerImage: team.settings.support?.csat?.headerImage
              ? new URL(team.settings.support.csat.headerImage)
              : undefined,
            bodyContent:
              team.settings.support?.csat?.surveyMessage ||
              DEFAULT_SURVEY_EMAIL_BODY_HTML,
            sampleRate: normalizeCsatSampleRate(
              team.settings.support?.csat?.sampleRate,
            ),
            allowedInboxEmails:
              team.settings.support?.csat?.allowedInboxEmails ?? [],
            emailTemplateId: team.settings.support?.csat?.emailTemplateId,
            abTestEnabled: team.settings.support?.csat?.abTestEnabled ?? false,
            abTestVariants: team.settings.support?.csat?.abTestVariants ?? [],
          }
        : undefined,
    [team],
  );

  if (!team || !value) {
    return null;
  }
  return <CsatSettingsForm value={value}>{children}</CsatSettingsForm>;
});

const CsatSettingsForm = memo(function CsatSettingsForm({
  value,
  children,
}: {
  value: CsatValue;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const reloadTeam = useRequiredContext(ReloadTeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const batchUpdateDefaultRuleConfigs = useBatchUpdateDefaultRuleConfigs();

  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const input = useInput(csatForm, value);
  const { bodyContent } = input.inputs;

  const templates = useCsatSurveyTemplates();
  const editor = useCsatSurveyMessageEditor({
    bodyContent: bodyContent.value,
    setBodyContent: bodyContent.setValue,
  });

  const canManageSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );

  const handleSave = async (signal: AbortSignal) => {
    const { team } = await getCurrentTeam(client, { signal });
    const { value } = input;
    if (!team.settings.support) {
      return;
    }

    const savedSurveyMessage = await editor.resolveSurveyMessage(
      value.bodyContent,
    );

    /* A template that cannot print the survey link silently falls back to the
       default layout at send time, so refuse the save. An id missing from the
       catalog is just as broken — but only judge that once the catalog has
       actually loaded, or a failed fetch would block every save. */
    const brokenTemplateIds = findUnsendableTemplateIds({
      abTestVariants: value.abTestVariants,
      catalogLoaded: templates.loaded,
      emailTemplateId: value.emailTemplateId,
      knownTemplateIds: new Set(
        templates.templates.map((template) => template.id),
      ),
      missingLinkIds: templates.missingLinkIds,
    });
    if (brokenTemplateIds.length > 0) {
      throw new Error(MISSING_SURVEY_LINK_MESSAGE);
    }

    await updateCurrentTeam(client, {
      team: {
        ...team,
        settings: {
          ...team.settings,
          support: {
            ...team.settings.support,
            csat: {
              enabled: value.csatEnabled,
              surveySendTime: value.surveySendTime,
              surveyMethods: value.surveyMethods,
              allowRemarks: value.allowRemarks,
              headerImage: value.headerImage?.href,
              surveyMessage: savedSurveyMessage,
              sampleRate: value.sampleRate,
              allowedInboxEmails: value.allowedInboxEmails,
              emailTemplateId: value.emailTemplateId,
              abTestEnabled: value.abTestEnabled,
              abTestVariants: value.abTestVariants,
            },
          },
        },
      },
    });

    await batchUpdateDefaultRuleConfigs([
      csatSettingsToSendSurveyConfig({
        enabled: value.csatEnabled,
        surveySendTime: value.surveySendTime,
        surveyMethods: value.surveyMethods,
        allowRemarks: value.allowRemarks,
        headerImage: value.headerImage?.href,
        surveyMessage: savedSurveyMessage,
        allowedInboxEmails: value.allowedInboxEmails,
        abTestEnabled: value.abTestEnabled,
        abTestVariants: value.abTestVariants,
      }),
    ]);

    reloadTeam();
    /* PageForm already surfaces failures through `alertOnFailure`; this is the
       matching confirmation for the success path. */
    toast(t("Customer satisfaction settings saved"), { variant: "success" });
  };

  const [previewTemplateId, setPreviewTemplateId] = useState<
    string | undefined
  >();

  const emailContent = csatSurveyEmail({
    headerImage: team.settings?.support?.csat?.headerImage || "",
    surveyLink: "",
    messageContent: bodyContent.value,
  });

  const contextValue = useMemo(
    () => ({
      inputs: input.inputs,
      templates,
      editor,
      canManageSupportSettings,
      openPreview: (templateId: string | undefined) => {
        setPreviewTemplateId(templateId);
        setPreviewModalOpen(true);
      },
    }),
    [input.inputs, templates, editor, canManageSupportSettings],
  );

  return (
    <ClientUploader>
      <CsatSettingsContext.Provider value={contextValue}>
        <PageForm
          initial={value}
          input={input}
          samePagePathPrefix={`/stores/${team._id}/support/csat`}
          save={handleSave}
        >
          <Flex grow={1} height="fit-content" justify="center">
            <div className={customerSatisfactionCss.contentContainer}>
              <SettingSectionWrapper id="support.csat">
                {/* Centers width-capped pages (General); full-width pages
                    are unaffected because they set `w="full"`. */}
                <Flex align="center" dir="column" gap="xl" w="full">
                  {children}
                </Flex>
              </SettingSectionWrapper>
            </div>
          </Flex>
        </PageForm>
      </CsatSettingsContext.Provider>
      <RedoModal
        isOpen={previewModalOpen}
        onModalCloseRequested={() => setPreviewModalOpen(false)}
        title="CSAT email preview"
      >
        {previewTemplateId === undefined ? (
          <div dangerouslySetInnerHTML={{ __html: emailContent }} />
        ) : (
          /* The default layout is rendered locally; saved templates are rendered
              through the email preview endpoint. */
          <EmailPreviewContent
            fullPreview
            key={previewTemplateId}
            templateId={previewTemplateId}
          />
        )}
      </RedoModal>
    </ClientUploader>
  );
});
